import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import { apiCall } from "../../api/apiCall";
import { GamificationSettings as GamificationSettingsType } from "../types";
import { getAuth, onAuthStateChanged } from "firebase/auth";

function GamificationSettings({
  translations,
  companyUuid,
}: {
  translations: any;
  companyUuid: string | null | undefined;
}) {
  const queryClient = useQueryClient();

  const [deadlineDay, setDeadlineDay] = useState<number>(10);
  const [isGamificationEnabled, setIsGamificationEnabled] =
    useState<boolean>(true);

  const [isAuthReady, setIsAuthReady] = useState(false);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setIsAuthReady(true);
    });
    return () => unsubscribe();
  }, []);

  const { data: currentSettings } = useQuery<GamificationSettingsType>({
    queryKey: ["gamificationSettings", companyUuid],
    queryFn: () =>
      apiCall<GamificationSettingsType>(
        `/admin/companies/${companyUuid}/gamification-settings`,
      ),
    enabled: Boolean(companyUuid) && isAuthReady,
  });

  useEffect(() => {
    if (currentSettings) {
      setDeadlineDay(currentSettings.expense_deadline_day ?? 10);
      setIsGamificationEnabled(currentSettings.is_gamification_enabled ?? true);
    }
  }, [currentSettings]);

  const updateSettingsMutation = useMutation({
    mutationFn: () =>
      apiCall(`/admin/companies/${companyUuid}/gamification-settings`, {
        method: "PATCH",
        body: JSON.stringify({
          expense_deadline_day: deadlineDay,
          is_gamification_enabled: isGamificationEnabled,
        }),
      }),
    onSuccess: () => {
      toast.success(
        translations?.updateSuccess ?? "Settings updated successfully",
      );
      queryClient.invalidateQueries({
        queryKey: ["gamificationSettings", companyUuid],
      });
    },
    onError: (error: Error) => {
      toast.error(
        (translations?.updateError ?? "Error: {message}").replace(
          "{message}",
          error.message,
        ),
      );
    },
  });

  const handleSaveSettings = () => {
    if (!companyUuid || updateSettingsMutation.isPending) return;
    updateSettingsMutation.mutate();
  };

  const handleDeadlineChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(event.target.value);
    setDeadlineDay(Math.min(31, Math.max(1, value)));
  };

  return (
    <section className="settings-card">
      <div className="settings-header">
        <h2 className="settings-title">
          {translations?.gamification?.title ?? "Gamification"}
        </h2>
        <p className="settings-text">
          {translations?.gamification?.description ?? ""}
        </p>
      </div>

      {!companyUuid ? (
        <p className="settings-text">
          {translations?.selectCompanyPrompt ??
            "Please select a company to continue."}
        </p>
      ) : (
        <div className="settings-form">
          <div className="setting-group">
            <label htmlFor="gamification-enabled">
              {translations?.gamification?.enabledLabel ??
                "Enable gamification"}
              <span>
                {translations?.gamification?.enabledDescription ?? ""}
              </span>
            </label>
            <label className="toggle-switch" htmlFor="gamification-enabled">
              <input
                id="gamification-enabled"
                type="checkbox"
                checked={isGamificationEnabled}
                onChange={(event) =>
                  setIsGamificationEnabled(event.target.checked)
                }
              />
              <span className="toggle-slider" />
            </label>
          </div>

          <div className="setting-group">
            <label htmlFor="expense-deadline-day">
              {translations?.gamification?.deadlineLabel ?? "Deadline day"}
              <span>
                {translations?.gamification?.deadlineDescription ?? ""}
              </span>
            </label>
            <input
              id="expense-deadline-day"
              type="number"
              min="1"
              max="31"
              className="setting-input-number"
              value={deadlineDay}
              onChange={handleDeadlineChange}
            />
          </div>

          <button
            type="button"
            className="save-settings-btn"
            onClick={handleSaveSettings}
            disabled={updateSettingsMutation.isPending || !companyUuid}
          >
            {updateSettingsMutation.isPending
              ? (translations?.saving ?? "Saving...")
              : (translations?.saveChanges ?? "Save changes")}
          </button>
        </div>
      )}
    </section>
  );
}

export default GamificationSettings;
